import api from '../utils/api';

/**
 * @typedef {Object} Module
 * @property {string} _id
 * @property {string} title
 * @property {string} course
 * @property {number} order
 * @property {string} [level]
 * @property {boolean} [isPublished]
 * @property {string} [completionCriteria]
 * @property {number} [completionPercent]
 * @property {string[]} lessons
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateModuleData
 * @property {string} title
 * @property {string} course
 * @property {number} [order]
 * @property {string} level
 * @property {boolean} [isPublished]
 * @property {string} [completionCriteria]
 * @property {number} [completionPercent]
 */

export const moduleService = {
  /**
   * Get all modules
   * @returns {Promise<{status: string, count: number, modules: Module[]}>}
   */
  getAllModules: async () => {
    const response = await api.get('/api/modules');
    return response;
  },

  /**
   * Get modules by course ID
   * @param {string} courseId 
   * @returns {Promise<{status: string, count: number, modules: Module[]}>}
   */
  getModulesByCourse: async (courseId) => {
    const response = await api.get(`/api/modules/course/${courseId}`);
    return response;
  },

  /**
   * Get module by ID
   * @param {string} id 
   * @returns {Promise<{status: string, module: Module}>}
   */
  getModuleById: async (id) => {
    const response = await api.get(`/api/modules/${id}`);
    return response;
  },

  /**
   * Create module (Instructor/Admin)
   * @param {CreateModuleData} moduleData 
   * @returns {Promise<{status: string, module: Module}>}
   */
  createModule: async (moduleData) => {
    const response = await api.post('/api/modules', moduleData);
    return response;
  },

  /**
   * Update module (Instructor/Admin)
   * @param {string} id 
   * @param {Object} updates 
   * @returns {Promise<{status: string, module: Module}>}
   */
  updateModule: async (id, updates) => {
    const response = await api.put(`/api/modules/${id}`, updates);
    return response;
  },

  /**
   * Delete module (Instructor/Admin)
   * @param {string} id 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteModule: async (id) => {
    const response = await api.delete(`/api/modules/${id}`);
    return response;
  },

  /**
   * Add lesson to module
   * @param {string} moduleId 
   * @param {string} lessonId 
   * @returns {Promise<{status: string, message: string}>}
   */
  addLessonToModule: async (moduleId, lessonId) => {
    const response = await api.post(`/api/modules/${moduleId}/lessons/${lessonId}`);
    return response;
  },

  /**
   * Remove lesson from module
   * @param {string} moduleId 
   * @param {string} lessonId 
   * @returns {Promise<{status: string, message: string}>}
   */
  removeLessonFromModule: async (moduleId, lessonId) => {
    const response = await api.delete(`/api/modules/${moduleId}/lessons/${lessonId}`);
    return response;
  },
};
